import { ApiResponse } from '@/interfaces/apiresponse.interface';
import { EngagementAccessDTO } from '@/interfaces/engagementAccessDTO.interface';
import { EngagementResponse } from '@/interfaces/engagementResponse.interface';
import { AppNav } from '@/routes/app-nav';
import { EngagementAccessService } from '@/services/engagement-access.service';
import { TitleService } from '@/services/title.service';
import Helpers from '@/shared/helpers';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Confirmation, ConfirmationService, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';

@Component({
  selector: 'app-access-management-dialog',
  standalone: false,
  templateUrl: './access-management-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./access-management-dialog.component.scss'],
})
export class AccessManagementDialogComponent {
  messageKey = 'engagementPage';
  loading: boolean = true;
  helpers: Helpers;

  @ViewChild(Table)
  usersTable!: Table;

  @Input()
  engagement?: EngagementResponse;

  @Input()
  public showing: boolean = false;

  @Output()
  public showingChange = new EventEmitter<boolean>();

  @Output()
  public onShouldUpdate = new EventEmitter();

  engagementAccessEntries: EngagementAccessDTO[] = [];

  constructor(
    private titleService: TitleService,
    private translocoService: TranslocoService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private engagementAccessService: EngagementAccessService
  ) {
    this.helpers = Helpers.getInstance(this.translocoService);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.engagement) {
      this.loadUserEntries();
    }
  }

  ngOnInit(): void {
    this.titleService.setTitle(AppNav.StatusMonitor.title);
    this.loadUserEntries();
  }

  loadUserEntries(): void {
    this.loading = true;
    if (!this.engagement) {
      return;
    }
    this.engagementAccessService.users(this.engagement.id).then(
      (response: HttpResponse<ApiResponse>) => {
        this.loading = false;
        const entries = (response.body || []) as EngagementAccessDTO[];
        this.engagementAccessEntries = entries;
      },
      (response: HttpErrorResponse) => {
        this.loading = false;
        this.helpers.showApiResponseMessage(
          response,
          this.messageService,
          this.messageKey
        );
      }
    );
  }

  confirmRevokeAccess(engagementAccess: EngagementAccessDTO) {
    const confirmObj: Confirmation = {
      ...this.helpers.getConfirmBase('defaultConfirmWithCheckbox'),
      header: this.translocoService.translate('confirm'),
      message: this.translocoService.translate('confirm_revoke_access'),
      acceptVisible: true,
      rejectVisible: true,
      closeOnEscape: true,
      accept: () => {
        this.revokeAccess(engagementAccess);
      },
      reject: () => {},
    };

    this.confirmationService.confirm(confirmObj);
  }

  revokeAccess(engagementAccess: EngagementAccessDTO) {
    if (!this.engagement) {
      return;
    }
    this.engagementAccessService
      .revokeAccess(this.engagement.id, engagementAccess.engagementUserId)
      .then(
        (response: HttpResponse<ApiResponse>) => {
          this.loading = false;
          this.loadUserEntries();
          this.showingChange.emit(false);
          this.onShouldUpdate.emit();
          this.helpers.showApiResponseMessage(
            response,
            this.messageService,
            this.messageKey
          );
        },
        (response: HttpErrorResponse) => {
          this.loading = false;
          this.helpers.showApiResponseMessage(
            response,
            this.messageService,
            this.messageKey
          );
        }
      )
      .finally(() => {
        this.confirmationService.close();
      });
  }
}
